<template>
  <!-- Native shell: a back bar instead of the marketing topbar — users arrive
       here from account/settings surfaces and need an obvious way back (the
       fixed topbar also collapses under the iOS safe-area inset). -->
  <AppHeader v-if="isNativeApp" :title="$t('navigation.deleteAccount')" show-back always-visible />
  <AppNavigation v-else mode="topbar"/>
  <div class="legal-container" :class="{ 'legal-container--web': !isNativeApp }">
    <div class="legal-content">
      <h1>{{ $t("deleteAccount.title") }}</h1>
      <p><strong>{{ $t("deleteAccount.lastModifiedLabel") }}</strong> {{ lastModified }}</p>

      <p>{{ $t("deleteAccount.intro") }}</p>

      <h2>{{ $t("deleteAccount.inApp.heading") }}</h2>
      <ol>
        <li>{{ $t("deleteAccount.inApp.step1") }}</li>
        <!-- i18n-t rather than a plain $t: the emphasised fragment is markup.
             scope="global" because this view is Options API and never calls
             useI18n(), matching OnboardingWizard.vue's waitlist banner. The
             emphasised labels are the ones the account screen already ships,
             so a user reading this page sees the same words they will tap. -->
        <i18n-t keypath="deleteAccount.inApp.step2" tag="li" scope="global">
          <template #tab><strong>{{ $t("navigation.account") }}</strong></template>
        </i18n-t>
        <i18n-t keypath="deleteAccount.inApp.step3" tag="li" scope="global">
          <template #section><strong>{{ $t("account.dangerZone.title") }}</strong></template>
          <template #action><strong>{{ $t("account.dangerZone.deleteAccount") }}</strong></template>
        </i18n-t>
        <li>{{ $t("deleteAccount.inApp.step4") }}</li>
      </ol>

      <h2>{{ $t("deleteAccount.onWeb.heading") }}</h2>
      <ol>
        <i18n-t keypath="deleteAccount.onWeb.step1" tag="li" scope="global">
          <template #site><a href="https://articulearnai.com">articulearnai.com</a></template>
        </i18n-t>
        <i18n-t keypath="deleteAccount.onWeb.step2" tag="li" scope="global">
          <template #link>
            <router-link to="/dashboard/account">{{ $t("deleteAccount.accountPageLink") }}</router-link>
          </template>
        </i18n-t>
        <i18n-t keypath="deleteAccount.onWeb.step3" tag="li" scope="global">
          <template #section><strong>{{ $t("account.dangerZone.title") }}</strong></template>
          <template #action><strong>{{ $t("account.dangerZone.deleteAccount") }}</strong></template>
        </i18n-t>
      </ol>

      <h2>{{ $t("deleteAccount.deleted.heading") }}</h2>
      <p>{{ $t("deleteAccount.deleted.intro") }}</p>
      <ul>
        <li>{{ $t("deleteAccount.deleted.profile") }}</li>
        <li>{{ $t("deleteAccount.deleted.conversations") }}</li>
        <li>{{ $t("deleteAccount.deleted.recordings") }}</li>
        <li>{{ $t("deleteAccount.deleted.billing") }}</li>
        <li>{{ $t("deleteAccount.deleted.contact") }}</li>
      </ul>
      <p>{{ $t("deleteAccount.deleted.permanent") }}</p>

      <h2>{{ $t("deleteAccount.retained.heading") }}</h2>
      <i18n-t keypath="deleteAccount.retained.body" tag="p" scope="global">
        <template #privacyPolicy>
          <router-link to="/privacy-policy">{{ $t("navigation.privacyPolicy") }}</router-link>
        </template>
      </i18n-t>

      <h2>{{ $t("deleteAccount.storeSubscriptions.heading") }}</h2>
      <p>{{ $t("deleteAccount.storeSubscriptions.body") }}</p>

      <h2>{{ $t("deleteAccount.cannotSignIn.heading") }}</h2>
      <i18n-t keypath="deleteAccount.cannotSignIn.body" tag="p" scope="global">
        <template #email>
          <a href="mailto:support@articulearnai.com">support@articulearnai.com</a>
        </template>
      </i18n-t>
    </div>
  </div>
  <FooterSection/>
</template>

<script>
import AppNavigation from "@/components/AppNavigation.vue";
import AppHeader from "@/components/AppHeader.vue";
import FooterSection from "@/components/FooterSection.vue";
import { computed } from "vue";
import { useHead } from "@unhead/vue";
import { authPresenceMixin } from "@/mixins/authPresenceMixin.js";
import { publicPageHead } from "@/scripts/publicHead.js";
import { formatLongDate } from "@/scripts/dateUtils.js";

export default {
  name: "DeleteAccountView",
  components: { AppNavigation, AppHeader, FooterSection },
  data() {
    return {
      isNativeApp: window.Capacitor?.isNativePlatform?.() === true,
    };
  },
  computed: {
    lastModified() {
      // "T00:00:00" pins the day to local midnight; parsing a bare
      // "YYYY-MM-DD" is UTC by spec and lands on the previous day west of
      // Greenwich. Same reasoning as the blog dates in Unit 7.
      return formatLongDate("2026-07-19T00:00:00", this.$i18n.locale);
    },
  },
  // Deliberately NOT authSetupMixin: signed-in users following these deletion
  // instructions must not be bounced to the dashboard by its auth listener.
  // The presence mixin keeps the mixin's other duty (authStore hydration)
  // without the redirect. Neither mixin seeds the first-sign-in profile; the
  // sign-in call sites in authUtils.js do.
  mixins: [authPresenceMixin],
  mounted() {
    // computed, not a plain string: locale bundles load lazily, so a value read
    // at mount can freeze the English fallback into the tab and the og: card.
    // No htmlAttrs.lang anywhere in here: <html lang> is owned by the locale
    // watcher in src/locales/index.js and describes the interface the reader is
    // using. Two writers would race, and the loser would be whichever ran last
    // on that particular navigation.
    useHead(
      publicPageHead({
        title: computed(() => this.$t("navigation.deleteAccount")),
        description: computed(() => this.$t("deleteAccount.metaDescription")),
        path: "/delete-account",
      }),
    );
  },
};
</script>

<style scoped>
.legal-container {
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem 4rem;
}

/* Web keeps the fixed marketing topbar, so clear it. --al-topbar-height
   resolves per breakpoint and includes the mobile safe-area inset; the
   native shell uses the sticky AppHeader, which takes its own layout space. */
.legal-container--web {
  padding-top: calc(var(--al-topbar-height) + 1.5rem);
}

.legal-content {
  max-width: 800px;
  width: 100%;
  color: #333;
  line-height: 1.7;
}

.legal-content h1 {
  font-size: 2rem;
  margin-bottom: 0.25rem;
}

.legal-content h2 {
  font-size: 1.3rem;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
}

.legal-content p,
.legal-content li {
  font-size: 0.95rem;
}

.legal-content ul,
.legal-content ol {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.legal-content a {
  color: #4a6cf7;
}
</style>
